import {
  BattleHistory,
  BattleHistoryEntry,
  DEFAULT_BATTLE_HISTORY,
  isValidBattleHistory,
} from '../types/BattleHistory';

/** LocalStorageのキー */
export const BATTLE_HISTORY_STORAGE_KEY = 'monster-buttle-battle-history';

/** 最大保存件数 */
export const MAX_HISTORY_ENTRIES = 100;

/**
 * モンスター別統計
 */
export interface MonsterStat {
  wins: number;
  losses: number;
  draws: number;
}

/**
 * バトル統計
 */
export interface BattleStats {
  /** 総戦数 */
  totalCount: number;
  /** 勝利数 */
  winCount: number;
  /** 敗北数 */
  loseCount: number;
  /** 引き分け数 */
  drawCount: number;
  /** 勝率（0〜100、小数点1位。drawは計算対象外） */
  winRate: number;
  /** モンスター別の勝敗数（キー: playerMonsterId） */
  monsterStats: Record<string, MonsterStat>;
}

/**
 * バトル履歴をLocalStorageから読み込む
 * データがない場合や壊れている場合はデフォルト値を返す
 */
export function loadBattleHistory(): BattleHistory {
  try {
    const stored = localStorage.getItem(BATTLE_HISTORY_STORAGE_KEY);
    if (!stored) return { ...DEFAULT_BATTLE_HISTORY, entries: [] };

    const parsed = JSON.parse(stored);
    if (!isValidBattleHistory(parsed)) return { ...DEFAULT_BATTLE_HISTORY, entries: [] };

    return parsed;
  } catch {
    return { ...DEFAULT_BATTLE_HISTORY, entries: [] };
  }
}

/**
 * バトル履歴をLocalStorageに保存する
 */
export function saveBattleHistory(history: BattleHistory): void {
  try {
    localStorage.setItem(BATTLE_HISTORY_STORAGE_KEY, JSON.stringify(history));
  } catch {
    // LocalStorageが使えない場合は無視
  }
}

/**
 * バトル履歴にエントリを追加する
 * id と date は自動生成される。最新エントリが先頭に追加される。
 * MAX_HISTORY_ENTRIES を超えた場合は古いエントリを削除する。
 */
export function addBattleHistoryEntry(
  entry: Omit<BattleHistoryEntry, 'id' | 'date'>
): BattleHistory {
  const history = loadBattleHistory();

  const newEntry: BattleHistoryEntry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    date: new Date().toISOString(),
    ...entry,
  };

  const entries = [newEntry, ...history.entries];

  // 上限超過分を切り捨て
  if (entries.length > MAX_HISTORY_ENTRIES) {
    entries.splice(MAX_HISTORY_ENTRIES);
  }

  const updated: BattleHistory = { entries };
  saveBattleHistory(updated);
  return updated;
}

/**
 * バトル履歴を全件削除する
 */
export function clearBattleHistory(): void {
  saveBattleHistory({ entries: [] });
}

/**
 * 最近の count 件のエントリを返す
 */
export function getRecentEntries(
  history: BattleHistory,
  count: number
): BattleHistoryEntry[] {
  return history.entries.slice(0, count);
}

/**
 * バトル統計を計算する
 */
export function calculateStats(history: BattleHistory): BattleStats {
  const { entries } = history;

  let winCount = 0;
  let loseCount = 0;
  let drawCount = 0;
  const monsterStats: Record<string, MonsterStat> = {};

  for (const entry of entries) {
    if (entry.resultType === 'PLAYER1_WIN') winCount++;
    else if (entry.resultType === 'PLAYER2_WIN') loseCount++;
    else drawCount++;

    const monsterId = entry.playerMonsterId;
    if (!monsterStats[monsterId]) {
      monsterStats[monsterId] = { wins: 0, losses: 0, draws: 0 };
    }
    if (entry.resultType === 'PLAYER1_WIN') monsterStats[monsterId].wins++;
    else if (entry.resultType === 'PLAYER2_WIN') monsterStats[monsterId].losses++;
    else monsterStats[monsterId].draws++;
  }

  const winLoseTotal = winCount + loseCount;
  const winRate =
    winLoseTotal === 0
      ? 0
      : Math.round((winCount / winLoseTotal) * 1000) / 10;

  return {
    totalCount: entries.length,
    winCount,
    loseCount,
    drawCount,
    winRate,
    monsterStats,
  };
}
